import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router'
import type { CSSProperties, ReactNode } from 'react'
import {
  AppShell,
  CommandPalette,
  CommandProvider,
  HeaderSlotProvider,
  useCommandRegistry,
} from '@/components/app'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { accessForPath, can } from '@/core/access'
import { useAssistantStatus } from '@/features/assistant/api'
import { AssistantLauncher } from '@/features/assistant/components/assistant-launcher'
import { requireSession } from '@/features/auth/guard'
import { RequestedScreen } from '@/features/auth/routes/requested-screen'
import { initialsOf, useMe, useSignOut } from '@/features/auth/use-me'
import { SandboxBanner } from '@/features/environments/components/sandbox-banner'
import { useImpersonation } from '@/features/impersonation/api'
import { ImpersonationBar } from '@/features/impersonation/components/impersonation-bar'
import { SearchResults } from '@/features/search/components/search-results'
import { ErrorScreen } from '@/features/shared/components/error-screen'
import { NoAccess } from '@/features/shared/components/no-access'
import { NotFound } from '@/features/shared/components/not-found'
import { TourOverlay } from '@/features/shared/components/tour/tour-overlay'
import { useEffectiveTestMode } from '@/features/shared/hooks/use-effective-test-mode'
import { useIdentify } from '@/features/shared/hooks/use-identify'
import { PageTours, TakeTheTourButton } from '@/features/shared/tours/page-tour-runner'
import { FeedAlarmBanner } from '@/features/webhooks/components/feed-alarm-banner'
import { isSandbox } from '@/lib/environment'
import { webhooksShown } from '@/lib/flags'
import { setTestMode } from '@/lib/test-mode'

export interface RouterContext {
  queryClient: QueryClient
}

/** Auth owns the whole viewport — no sidebar, no tab bar (DESIGN.md). */
const AUTH_ROUTES = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/accept-invitation',
  '/create-account',
  '/confirm-email',
]

/**
 * Reachable without a session: the checklist explains why you cannot sign in
 * yet, and the page connecting an AI app lands on when it goes wrong may be
 * read signed out (docs/MCP.md).
 */
const PUBLIC_ROUTES = [...AUTH_ROUTES, '/setup', '/oauth/error']

/**
 * Screens that own the whole viewport — no sidebar, no tab bar. Auth, the
 * approve-an-AI-app screens (the consent screen needs a session, but looks
 * like sign-in), and the setup checklist: all are read before or outside the
 * app proper, so the shell around them is meaningless.
 */
const BARE_ROUTES = [...AUTH_ROUTES, '/setup', '/oauth/consent', '/oauth/error']

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location, context }) => {
    // Auth routes are public; everything else needs a session when auth is on.
    if (PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route))) return
    // The query string comes along, so signing in lands back on the same
    // page — an AI app's signed consent link included (docs/MCP.md).
    await requireSession(`${location.pathname}${location.searchStr ?? ''}`, context.queryClient)
  },
  component: RootLayout,
  // A crash while rendering any screen lands here instead of a blank page.
  errorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
  // An address that isn't a page — instead of the router's bare "Not Found".
  notFoundComponent: NotFound,
})

function RootLayout() {
  return (
    // shadcn's sidebar renders Tooltips for the collapsed icon rail; Base UI's
    // tooltip needs this provider or it throws on an undefined store.
    <TooltipProvider>
      <CommandProvider>
        <Layout />
        <CommandPalette
          records={(query, close) => <SearchResults query={query} onOpen={close} />}
        />
        <Toaster />
        {/* "Show me" on System → Change, and each page's first-visit tour; it
            outlives the screens it visits, and draws nothing until one starts. */}
        <TourOverlay />
      </CommandProvider>
    </TooltipProvider>
  )
}

function Layout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { setOpen } = useCommandRegistry()
  // Not asked on the bare screens (sign-in, accepting an invitation…): asking
  // "who am I" creates a personal workspace for a new account, which someone
  // arriving to join an existing workspace should not get as a side effect.
  const bare = BARE_ROUTES.some((route) => pathname.startsWith(route))
  const me = useMe({ enabled: !bare })
  const signOut = useSignOut()
  const impersonation = useImpersonation()
  const queryClient = useQueryClient()
  const { testMode, canToggle } = useEffectiveTestMode({ enabled: !bare })
  // A crash report with no person attached is a crash report you cannot chase.
  useIdentify(me.data)

  // The assistant — for anyone whose role includes it, and never while
  // impersonating: it only ever acts for the person signed in.
  const role = me.data?.role
  const assistantEligible = Boolean(role && can(role, 'assistant.use') && !me.data?.impersonation)
  // Whether a model is configured at all, not just available to this person
  // right now — separate from Test mode, Test mode never fakes this (docs/
  // ASSISTANT.md). While it's loading, treat it as not connected: showing the
  // bubble and then pulling it away is worse than a short delay before it
  // appears.
  const assistantStatus = useAssistantStatus(assistantEligible)
  const assistantConnected = assistantStatus.data?.connected === true

  // A sandbox build says so on every page, signed in or not (docs/ENVIRONMENTS.md).
  // What's waiting for approval is change-log content, so only for those who
  // can read the change log on System.
  const environmentBanner = isSandbox ? (
    <SandboxBanner showChanges={Boolean(role && can(role, 'system.view'))} />
  ) : undefined

  if (BARE_ROUTES.some((route) => pathname.startsWith(route))) {
    return (
      <WithEnvironmentBanner banner={environmentBanner}>
        <Outlet />
      </WithEnvironmentBanner>
    )
  }

  // Signed up without an invitation: a single waiting page, no shell at all,
  // until an admin chooses their role (ADR single-organization).
  if (me.data?.role === 'requested') {
    return (
      <WithEnvironmentBanner banner={environmentBanner}>
        <RequestedScreen organizationName={me.data.organizationName} email={me.data.email} />
      </WithEnvironmentBanner>
    )
  }

  // The page's permission, from src/core/access/routes.ts. Decided once here,
  // so no screen has to remember to check. The server refuses the data too.
  const access = accessForPath(pathname)
  const allowed = access === 'public' || (role !== undefined && can(role, access))

  return (
    <HeaderSlotProvider>
      {(headerCenter) => (
        <AppShell
          onOpenCommandPalette={() => setOpen(true)}
          // Signing out while impersonating would strand the admin signed out;
          // it ends the impersonation instead and takes them back.
          onSignOut={me.data?.impersonation ? impersonation.stop : signOut}
          floating={assistantEligible && assistantConnected ? <AssistantLauncher /> : undefined}
          environmentBanner={environmentBanner}
          headerCenter={headerCenter}
          help={allowed && me.data ? <TakeTheTourButton /> : undefined}
          // Admins see a webhook that has gone quiet on every page but the
          // Webhooks pages, which already say so.
          notice={
            <FeedAlarmBanner
              enabled={Boolean(
                webhooksShown &&
                  role &&
                  can(role, 'settings.manage') &&
                  !me.data?.impersonation &&
                  !pathname.startsWith('/settings/webhooks'),
              )}
            />
          }
          banner={
            me.data?.impersonation ? (
              <ImpersonationBar name={me.data.name} expiresAt={me.data.impersonation.expiresAt} />
            ) : undefined
          }
          testMode={{
            on: testMode,
            canToggle,
            onChange: (on) => {
              setTestMode(on)
              // Re-ask the server for everything: the same screens, other data.
              void queryClient.resetQueries()
            },
          }}
          account={
            me.data
              ? {
                  name: me.data.name,
                  email: me.data.email,
                  initials: initialsOf(me.data.name, me.data.email),
                  role: me.data.role,
                }
              : undefined
          }
        >
          {/* While "who am I" is loading, render nothing rather than flash either. */}
          {allowed ? <Outlet /> : role ? <NoAccess role={role} /> : null}
          {/* Not while an Admin acts as someone: it would use up their first visits. */}
          <PageTours enabled={allowed && Boolean(me.data) && !me.data?.impersonation} />
        </AppShell>
      )}
    </HeaderSlotProvider>
  )
}

/**
 * The screens without the shell (sign-in, setup, the waiting room) still show
 * the Sandbox strip. It sits in the flow at the top, and `--top-banner` tells
 * the full-height auth layout to leave room for it rather than scroll.
 */
function WithEnvironmentBanner({ banner, children }: { banner?: ReactNode; children: ReactNode }) {
  if (!banner) return <>{children}</>
  return (
    <div style={{ '--top-banner': '2.75rem' } as CSSProperties}>
      <div className="sticky top-0 z-40">{banner}</div>
      {children}
    </div>
  )
}
